import React, { useState } from 'react'
import ScrollableImageGallery from './chatbot/queries/ScrollableImageGallery';

function formatText(text) {
  // Updated regex to include www URLs and http/https URLs
  return text.split(/(\*\*.*?\*\*|\*.*?\*|`.*?`|__.*?__|~~.*?~~|\[.*?\]\(.*?\)|(?:https?:\/\/|www\.)[^\s]+)/).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    } else if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    } else if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={index} className="krunk-bg-gray-100 krunk-px-1 krunk-rounded">{part.slice(1, -1)}</code>;
    } else if (part.startsWith('__') && part.endsWith('__')) {
      return <u key={index}>{part.slice(2, -2)}</u>;
    } else if (part.startsWith('~~') && part.endsWith('~~')) {
      return <del key={index}>{part.slice(2, -2)}</del>;
    } else if (part.match(/\[.*?\]\(.*?\)/)) {
      const match = part.match(/\[(.*?)\]\((.*?)\)/);
      return <a 
        key={index} 
        href={match[2]} 
        className="krunk-text-blue-500 krunk-underline krunk-hover:text-blue-700"
        target="_blank"
        rel="noopener noreferrer"
      >
        {match[1]}
      </a>;
    } else if (part.match(/^(?:https?:\/\/|www\.)[^\s]+$/)) {
      // Handle both http/https URLs and www URLs
      const href = part.startsWith('www.') ? `https://${part}` : part;
      return <a 
        key={index} 
        href={href} 
        className="krunk-text-blue-500 krunk-underline krunk-hover:text-blue-700"
        target="_blank"
        rel="noopener noreferrer"
      >
        Click here
      </a>;
    }
    return part;
  });
}

function FormattedText({ text = "" }) {
  // Handle null, undefined, or non-string values
  if (!text || typeof text !== 'string') {
    return <div></div>;
  }

  return (
    <div>
      {text.split('\n').map((line, index) => (
        <p key={index} className="krunk-w-full krunk-p-2 krunk-m-0 krunk-break-words">
          {formatText(line)}
        </p>
      ))}
    </div>
  );
}

// Image Modal Component
function ImageModal({ isOpen, imageUrl, onClose }) {
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div 
      className="krunk-fixed krunk-inset-0 krunk-bg-black krunk-bg-opacity-80 krunk-flex krunk-items-center krunk-justify-center krunk-z-50 krunk-p-4"
      onClick={handleBackdropClick}
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999
      }}
    >
      <div className="krunk-relative krunk-max-w-full krunk-max-h-full">
        <img 
          src={imageUrl} 
          alt="Full size view" 
          className="krunk-max-w-full krunk-max-h-full krunk-object-contain krunk-rounded-lg krunk-shadow-2xl"
        />
        <button 
          onClick={onClose}
          className="krunk-absolute krunk-top-4 krunk-right-4 krunk-bg-black krunk-bg-opacity-50 krunk-text-white krunk-rounded-full krunk-w-10 krunk-h-10 krunk-flex krunk-items-center krunk-justify-center krunk-hover:bg-opacity-70 krunk-transition-all"
          aria-label="Close image"
        >
          ×
        </button>
      </div>
    </div>
  );
}

function MediaContainer({ mediaType, text }) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  switch (mediaType) {
    case 'image':
      return (
        <>
          <div className="media-container image-container">
            <img 
              src={text} 
              alt="Chat image" 
              className="krunk-max-w-full krunk-h-auto krunk-rounded-lg krunk-cursor-pointer krunk-hover:opacity-90 krunk-transition-opacity"
              onClick={openModal}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
              title="Click to view full size"
            />
            <div className="error-fallback" style={{ display: 'none' }}>
              <p className="krunk-text-red-500">Failed to load image: {text}</p>
            </div>
          </div>
          
        </>
      );
    
    case 'video':
      return (
        <div className="media-container video-container">
          <video 
            src={text} 
            controls 
            className="krunk-max-w-full krunk-h-auto krunk-rounded-lg"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          >
            Your browser does not support the video tag.
          </video>
          <div className="error-fallback" style={{ display: 'none' }}>
            <p className="krunk-text-red-500">Failed to load video: {text}</p>
          </div>
        </div>
      );
    
    case 'document':
      return (
        <div className="media-container document-container">
          <div className="document-preview krunk-bg-gray-50 krunk-p-4 krunk-rounded-lg krunk-border">
            <div className="document-icon krunk-mb-2">
              📄
            </div>
            <div className="document-info">
              <p className="krunk-font-medium krunk-text-gray-800">
                {text.split('/').pop() || 'Document'}
              </p>
              <a 
                href={text} 
                target="_blank" 
                rel="noopener noreferrer"
                className="krunk-text-blue-500 krunk-underline krunk-hover:text-blue-700 krunk-text-sm"
              >
                Open Document
              </a>
            </div>
          </div>
        </div>
      );
    
    case 'audio':
      return (
        <div className="media-container audio-container">
          <audio 
            src={text} 
            controls 
            className="krunk-w-full"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'block';
            }}
          >
            Your browser does not support the audio element.
          </audio>
          <div className="error-fallback" style={{ display: 'none' }}>
            <p className="krunk-text-red-500">Failed to load audio: {text}</p>
          </div>
        </div>
      );
    
    default:
      return <FormattedText text={text} />;
  }
}

function chat(props) {
  const { mediatype = 'text', message, time } = props;
   if (!message?.text || message.text.trim() === '') {
    return null;
  }
  
return (
<div
  className="krunk-flex krunk-flex-col krunk-items-start krunk-mt-2 krunk-pt-0 krunk-pb-0 krunk-text-[13px] krunk-font-normal krunk-font-['Poppins']"
  style={{ color: 'var(--tailwind-buttontext)' }}
>
  <div
    className="krunk-w-4/5 krunk-p-[7px] krunk-rounded-2xl krunk-bg-headercolor krunk-text-bubbletext"
    style={{
      borderRadius: '0 8px 8px 8px',
    }}
  >
    {mediatype === 'text' ? (
      <FormattedText text={message.text} />
    ) : (
      <MediaContainer mediaType={mediatype} text={message.text} />
    )}
  </div>
</div>

);
}

export default chat